import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LucideIcon from "../../../components/LucideIcon";
import { LineChart } from "@mui/x-charts/LineChart";
import axios from "axios";
import { hostname, parseError } from "../../../utils";
import { Campaign } from "./CampaignFormDialog";
import {
  MARKETING_CAMPAIGN_STATUS_CANCELLED,
  MARKETING_CAMPAIGN_STATUS_DRAFT,
  MARKETING_CAMPAIGN_STATUS_FAILED,
  MARKETING_CAMPAIGN_STATUS_SCHEDULED,
  MARKETING_CAMPAIGN_STATUS_SENDING,
  MARKETING_CAMPAIGN_STATUS_SENT,
  MARKETING_RECIPIENT_STATUS_FAILED,
  MARKETING_RECIPIENT_STATUS_PENDING,
  MARKETING_RECIPIENT_STATUS_SENT,
  MARKETING_RECIPIENT_STATUS_SKIPPED,
} from "../../../constants";

interface TimeseriesSeries {
  id: string;
  name: string;
  label: string;
  data: number[];
}

interface TimeseriesResponse {
  dates: string[];
  series: TimeseriesSeries[];
}

const SERIES_COLORS: Record<string, string> = {
  sent: "#1976d2",
  opens: "#4caf50",
  clicks: "#ff9800",
};

const formatHourTick = (iso: string) => {
  const d = new Date(iso);
  if (d.getHours() === 0) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return "";
};

const formatHourTooltip = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
  });
};

interface RecipientMetrics {
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  opened: number;
  clicked: number;
  openRate: number;
  clickRate: number;
}

interface Recipient {
  id: string;
  email?: string | null;
  status: string;
  sentAt?: string | null;
  errorMessage?: string | null;
  opened?: boolean;
  firstOpenedDate?: string | null;
  lastOpenedDate?: string | null;
  openCount?: number;
  clicked?: boolean;
  firstClickedDate?: string | null;
  lastClickedDate?: string | null;
  clickCount?: number;
  created_date?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  campaign: Campaign | null;
  companyId: string;
  getToken: () => Promise<string>;
  onError: (err: string) => void;
}

const RECIPIENT_STATUS_COLORS: Record<
  string,
  "default" | "primary" | "warning" | "error"
> = {
  [MARKETING_RECIPIENT_STATUS_PENDING]: "default",
  [MARKETING_RECIPIENT_STATUS_SENT]: "primary",
  [MARKETING_RECIPIENT_STATUS_FAILED]: "error",
  [MARKETING_RECIPIENT_STATUS_SKIPPED]: "warning",
};

const CAMPAIGN_STATUS_LABELS: Record<string, string> = {
  [MARKETING_CAMPAIGN_STATUS_DRAFT]: "Draft",
  [MARKETING_CAMPAIGN_STATUS_SCHEDULED]: "Scheduled",
  [MARKETING_CAMPAIGN_STATUS_SENDING]: "Sending",
  [MARKETING_CAMPAIGN_STATUS_SENT]: "Sent",
  [MARKETING_CAMPAIGN_STATUS_FAILED]: "Failed",
  [MARKETING_CAMPAIGN_STATUS_CANCELLED]: "Cancelled",
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatPercent = (rate: number): string => `${(rate * 100).toFixed(1)}%`;

const CompactMetric: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string;
  tooltip: string;
}> = ({ icon, title, value, tooltip }) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
        <Box
          sx={{
            color: "text.secondary",
            display: "flex",
            "& svg": { width: 16, height: 16 },
          }}
        >
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  </Tooltip>
);

interface TimelineEvent {
  id: string;
  label: string;
  timestamp?: string | null;
  description?: string;
  icon: React.ReactNode;
  color: "primary" | "success" | "warning" | "error" | "default";
}

const TIMELINE_COLOR_TOKENS: Record<TimelineEvent["color"], string> = {
  primary: "primary.main",
  success: "primary.main",
  warning: "warning.main",
  error: "error.main",
  default: "text.disabled",
};

const CampaignDetailDialog: React.FC<Props> = ({
  open,
  onClose,
  campaign,
  companyId,
  getToken,
  onError,
}) => {
  const PAGE_SIZE = 50;
  const [tab, setTab] = useState(0);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [recipientMetrics, setRecipientMetrics] =
    useState<RecipientMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [timeseries, setTimeseries] = useState<TimeseriesResponse | null>(null);
  const [timelineLimit, setTimelineLimit] = useState(PAGE_SIZE);
  const [recipientsPage, setRecipientsPage] = useState(0);
  const [recipientsRowsPerPage, setRecipientsRowsPerPage] = useState(25);
  const [recipientsSearchInput, setRecipientsSearchInput] = useState("");
  const [recipientsSearch, setRecipientsSearch] = useState("");
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState<Recipient | null>(
    null
  );
  const [recipientDeleting, setRecipientDeleting] = useState(false);
  const timelineSentinel = useRef<HTMLDivElement | null>(null);

  const fetchRecipients = useCallback(
    async (search: string) => {
      if (!campaign?.id) return;
      setRecipientsLoading(true);
      try {
        const token = await getToken();
        const recipientsRes = await axios.get(
          `${hostname}/marketing-campaigns/${campaign.id}/recipients`,
          {
            params: { companyId, ...(search ? { search } : {}) },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setRecipients(recipientsRes.data?.items || []);
        setRecipientMetrics(recipientsRes.data?.metrics ?? null);
      } catch (err) {
        onError(parseError(err));
      } finally {
        setRecipientsLoading(false);
      }
    },
    [campaign?.id, companyId, getToken, onError]
  );

  const handleConfirmDeleteRecipient = useCallback(async () => {
    if (!campaign?.id || !recipientToDelete) return;
    setRecipientDeleting(true);
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/marketing-campaigns/${campaign.id}/recipients/${recipientToDelete.id}`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setRecipientToDelete(null);
      await fetchRecipients(recipientsSearch);
    } catch (err) {
      onError(parseError(err));
    } finally {
      setRecipientDeleting(false);
    }
  }, [
    campaign?.id,
    companyId,
    getToken,
    onError,
    recipientToDelete,
    fetchRecipients,
    recipientsSearch,
  ]);

  const loadData = useCallback(async () => {
    if (!campaign?.id) return;
    setLoading(true);
    try {
      const token = await getToken();
      const seriesRes = await axios.get(
        `${hostname}/marketing-campaigns/timeseries`,
        {
          params: { companyId, campaignId: campaign.id, days: 7 },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTimeseries(seriesRes.data ?? null);
    } catch (err) {
      onError(parseError(err));
    } finally {
      setLoading(false);
    }
  }, [campaign?.id, companyId, getToken, onError]);

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setRecipients([]);
    setRecipientMetrics(null);
    setTimeseries(null);
    setTimelineLimit(PAGE_SIZE);
    setRecipientsPage(0);
    setRecipientsSearchInput("");
    setRecipientsSearch("");
    if (campaign?.id) {
      loadData();
    }
  }, [open, campaign?.id, loadData]);

  const debouncedSetRecipientsSearch = useMemo(
    () =>
      debounce((value: string) => {
        setRecipientsSearch(value);
        setRecipientsPage(0);
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetRecipientsSearch.clear();
    };
  }, [debouncedSetRecipientsSearch]);

  useEffect(() => {
    if (!open || !campaign?.id) return;
    fetchRecipients(recipientsSearch);
  }, [open, campaign?.id, recipientsSearch, fetchRecipients]);

  useEffect(() => {
    setTimelineLimit(PAGE_SIZE);
  }, [tab]);

  useEffect(() => {
    const node = timelineSentinel.current;
    if (!node || tab !== 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setTimelineLimit((l) => l + PAGE_SIZE);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [tab, timelineLimit]);

  const derivedMetrics = useMemo(() => {
    const total =
      recipientMetrics?.total ??
      campaign?.totalRecipients ??
      recipients.length;
    const sent =
      recipientMetrics?.sent ??
      campaign?.sentCount ??
      recipients.filter((r) => r.status === MARKETING_RECIPIENT_STATUS_SENT).length;
    const failed =
      recipientMetrics?.failed ??
      campaign?.failedCount ??
      recipients.filter((r) => r.status === MARKETING_RECIPIENT_STATUS_FAILED).length;
    const skipped =
      recipientMetrics?.skipped ??
      campaign?.skippedCount ??
      recipients.filter((r) => r.status === MARKETING_RECIPIENT_STATUS_SKIPPED).length;
    const opened =
      recipientMetrics?.opened ??
      recipients.filter((r) => r.opened).length;
    const clicked =
      recipientMetrics?.clicked ??
      recipients.filter((r) => r.clicked).length;
    const openRate =
      recipientMetrics?.openRate ?? (sent > 0 ? opened / sent : 0);
    const clickRate =
      recipientMetrics?.clickRate ?? (sent > 0 ? clicked / sent : 0);
    return { total, sent, failed, skipped, opened, clicked, openRate, clickRate };
  }, [campaign, recipients, recipientMetrics]);

  const timeline = useMemo<TimelineEvent[]>(() => {
    if (!campaign) return [];
    const events: TimelineEvent[] = [];
    const created = (campaign as any).created_date;
    if (created) {
      events.push({
        id: "created",
        label: "Campaign created",
        timestamp: created,
        icon: <LucideIcon name="Pencil" size={18} />,
        color: "default",
      });
    }
    if (campaign.scheduledAt) {
      events.push({
        id: "scheduled",
        label: "Scheduled to send",
        timestamp: campaign.scheduledAt,
        description: campaign.timezone
          ? `Timezone ${campaign.timezone}`
          : undefined,
        icon: <LucideIcon name="Clock" size={18} />,
        color: "primary",
      });
    }
    const startedAt = (campaign as any).startedAt;
    if (startedAt) {
      events.push({
        id: "started",
        label: "Sending started",
        timestamp: startedAt,
        icon: <LucideIcon name="CirclePlay" size={18} />,
        color: "primary",
      });
    }
    const completedAt = (campaign as any).completedAt;
    if (completedAt && campaign.status === MARKETING_CAMPAIGN_STATUS_SENT) {
      events.push({
        id: "completed",
        label: "Sending completed",
        timestamp: completedAt,
        description: `${derivedMetrics.sent} sent · ${derivedMetrics.failed} failed · ${derivedMetrics.skipped} skipped`,
        icon: <LucideIcon name="CheckCheck" size={18} />,
        color: "success",
      });
    }
    if (campaign.status === MARKETING_CAMPAIGN_STATUS_FAILED && completedAt) {
      events.push({
        id: "failed",
        label: "Campaign failed",
        timestamp: completedAt,
        icon: <LucideIcon name="CircleAlert" size={18} />,
        color: "error",
      });
    }
    if (campaign.status === MARKETING_CAMPAIGN_STATUS_CANCELLED) {
      events.push({
        id: "cancelled",
        label: "Campaign cancelled",
        timestamp: (campaign as any).updated_date,
        icon: <LucideIcon name="CircleX" size={18} />,
        color: "warning",
      });
    }

    for (const r of recipients) {
      const emailLabel = r.email || "(no email)";
      if (r.sentAt && r.status === MARKETING_RECIPIENT_STATUS_SENT) {
        events.push({
          id: `sent-${r.id}`,
          label: `Sent to ${emailLabel}`,
          timestamp: r.sentAt,
          icon: <LucideIcon name="Send" size={18} />,
          color: "primary",
        });
      }
      if (r.status === MARKETING_RECIPIENT_STATUS_FAILED) {
        events.push({
          id: `failed-${r.id}`,
          label: `Failed to send to ${emailLabel}`,
          timestamp: r.sentAt || r.created_date,
          description: r.errorMessage || undefined,
          icon: <LucideIcon name="CircleAlert" size={18} />,
          color: "error",
        });
      }
      if (r.lastOpenedDate) {
        const count = r.openCount || 0;
        events.push({
          id: `open-${r.id}`,
          label: `${emailLabel} opened the email`,
          timestamp: r.lastOpenedDate,
          description:
            count > 1
              ? `${count} opens · first ${formatDate(r.firstOpenedDate)}`
              : undefined,
          icon: <LucideIcon name="Eye" size={18} />,
          color: "success",
        });
      }
      if (r.lastClickedDate) {
        const count = r.clickCount || 0;
        events.push({
          id: `click-${r.id}`,
          label: `${emailLabel} clicked a link`,
          timestamp: r.lastClickedDate,
          description:
            count > 1
              ? `${count} clicks · first ${formatDate(r.firstClickedDate)}`
              : undefined,
          icon: <LucideIcon name="Pointer" size={18} />,
          color: "success",
        });
      }
    }

    return events.sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return ta - tb;
    });
  }, [campaign, recipients, derivedMetrics]);

  if (!campaign) return null;

  return (
    <>
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="h6"
            component="span"
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {campaign.name}
          </Typography>
          <Chip
            size="small"
            label={CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
          />
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <LucideIcon name="X" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Send" />}
              title="Sent"
              value={String(derivedMetrics.sent)}
              tooltip="Recipients successfully sent"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Eye" />}
              title="Opens"
              value={String(derivedMetrics.opened)}
              tooltip="Unique recipients who opened the email"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Pointer" />}
              title="Clicks"
              value={String(derivedMetrics.clicked)}
              tooltip="Unique recipients who clicked a link"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Percent" />}
              title="Open Rate"
              value={formatPercent(derivedMetrics.openRate)}
              tooltip="Opens divided by sent"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Percent" />}
              title="Click Rate"
              value={formatPercent(derivedMetrics.clickRate)}
              tooltip="Clicks divided by sent"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="CircleAlert" />}
              title="Failed"
              value={String(derivedMetrics.failed)}
              tooltip="Recipients that errored during send"
            />
          </Grid>
        </Grid>

        {(() => {
          const chartSeries =
            timeseries?.series.map((s) => ({
              label: s.label,
              data: s.data,
              color: SERIES_COLORS[s.id] ?? "#1976d2",
              stack: "events",
              area: true,
              showMark: false,
              curve: "monotoneX" as const,
            })) ?? [];
          const chartDates = timeseries?.dates ?? [];
          const hasChartData = chartSeries.some((s) =>
            s.data.some((v) => v > 0)
          );
          return (
            <Paper elevation={1} sx={{ px: 1.5, py: 1, mb: 2 }}>
              {hasChartData ? (
                <Box sx={{ width: "100%", height: 160 }}>
                  <LineChart
                    xAxis={[
                      {
                        scaleType: "point",
                        data: chartDates,
                        valueFormatter: (value: string, context: any) =>
                          context?.location === "tooltip"
                            ? formatHourTooltip(value)
                            : formatHourTick(value),
                        tickLabelInterval: (value: string) => {
                          const d = new Date(value);
                          return d.getHours() === 0;
                        },
                      },
                    ]}
                    yAxis={[{ disableLine: true, disableTicks: true }]}
                    series={chartSeries}
                    height={160}
                    margin={{ top: 10, right: 10, bottom: 24, left: 10 }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={{
                      "& .MuiAreaElement-root": { fillOpacity: 0.35 },
                      "& .MuiChartsAxis-tickLabel": { fontSize: "0.7rem" },
                      "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
                        display: "none",
                      },
                    }}
                  />
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", py: 1, textAlign: "center" }}
                >
                  No activity for this campaign in the last 7 days yet.
                </Typography>
              )}
            </Paper>
          );
        })()}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab label="Timeline" />
          <Tab label={`Recipients (${derivedMetrics.total})`} />
          <Tab label="Email Preview" />
        </Tabs>

        {tab === 0 && (
          <Box>
            {timeline.length === 0 ? (
              <Alert severity="info">
                No timeline events yet. Events will appear as the campaign is
                scheduled, sent, and engaged with.
              </Alert>
            ) : (
              <Stack spacing={0}>
                {timeline.slice(0, timelineLimit).map((event, idx) => (
                  <Box
                    key={event.id}
                    sx={{ display: "flex", gap: 2, position: "relative" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: 32,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          backgroundColor: TIMELINE_COLOR_TOKENS[event.color],
                          color: "common.white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "& svg": { width: 16, height: 16 },
                        }}
                      >
                        {event.icon}
                      </Box>
                      {idx < Math.min(timeline.length, timelineLimit) - 1 && (
                        <Box
                          sx={{
                            flexGrow: 1,
                            width: 2,
                            backgroundColor: "divider",
                            minHeight: 24,
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ pb: 2, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {event.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(event.timestamp)}
                      </Typography>
                      {event.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {event.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
                {timelineLimit < timeline.length && (
                  <Box
                    ref={timelineSentinel}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 2,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search recipients by email..."
              value={recipientsSearchInput}
              onChange={(e) => {
                const value = e.target.value;
                setRecipientsSearchInput(value);
                debouncedSetRecipientsSearch(value);
              }}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" sx={{ color: "action.active", display: "inline-flex" }}>
                      <LucideIcon name="Search" size={18} />
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: recipientsSearchInput ? (
                  <InputAdornment position="end">
                    {recipientsLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => {
                          setRecipientsSearchInput("");
                          debouncedSetRecipientsSearch.clear();
                          setRecipientsSearch("");
                          setRecipientsPage(0);
                        }}
                      >
                        <LucideIcon name="X" size={18} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ) : recipientsLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
            {recipientsLoading && recipients.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : recipients.length === 0 ? (
              <Alert severity="info">
                {recipientsSearch
                  ? `No recipients match "${recipientsSearch}".`
                  : "No recipients yet. They will appear once the campaign starts sending."}
              </Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent</TableCell>
                        <TableCell>Opens</TableCell>
                        <TableCell>Clicks</TableCell>
                        <TableCell>Error</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {recipients
                      .slice(
                        recipientsPage * recipientsRowsPerPage,
                        recipientsPage * recipientsRowsPerPage +
                          recipientsRowsPerPage
                      )
                      .map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.email || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.status}
                            color={RECIPIENT_STATUS_COLORS[r.status] || "default"}
                          />
                        </TableCell>
                        <TableCell>{formatDate(r.sentAt)}</TableCell>
                        <TableCell>
                          {r.opened ? (
                            <Tooltip
                              arrow
                              title={`Last: ${formatDate(r.lastOpenedDate)}`}
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box component="span" sx={{ color: "primary.main", display: "inline-flex" }}>
                                  <LucideIcon name="Eye" size={18} />
                                </Box>
                                <Typography variant="body2">
                                  {r.openCount || 0}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {r.clicked ? (
                            <Tooltip
                              arrow
                              title={`Last: ${formatDate(r.lastClickedDate)}`}
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box component="span" sx={{ color: "primary.main", display: "inline-flex" }}>
                                  <LucideIcon name="Pointer" size={18} />
                                </Box>
                                <Typography variant="body2">
                                  {r.clickCount || 0}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.errorMessage ? (
                            <Tooltip arrow title={r.errorMessage}>
                              <Typography variant="caption" color="error">
                                {r.errorMessage}
                              </Typography>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip arrow title="Remove recipient">
                            <IconButton
                              size="small"
                              onClick={() => setRecipientToDelete(r)}
                            >
                              <LucideIcon name="Trash2" size={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={recipients.length}
                  rowsPerPage={recipientsRowsPerPage}
                  page={recipientsPage}
                  onPageChange={(_, newPage) => setRecipientsPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRecipientsRowsPerPage(parseInt(e.target.value, 10));
                    setRecipientsPage(0);
                  }}
                  sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                />
              </Paper>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box component="span" sx={{ color: "action.active", display: "inline-flex" }}>
                  <LucideIcon name="Mail" size={18} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  From:
                </Typography>
                <Typography variant="body2">
                  {campaign.fromName
                    ? `${campaign.fromName} <${
                        campaign.senderProfile?.email || ""
                      }>`
                    : campaign.senderProfile?.name
                    ? `${campaign.senderProfile.name} <${campaign.senderProfile.email}>`
                    : campaign.senderProfile?.email || "—"}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="body2" color="text.secondary">
                  Subject:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {campaign.subject}
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                backgroundColor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                maxHeight: 500,
                overflow: "auto",
                "& img": { maxWidth: "100%" },
                "& a": { color: "primary.main" },
              }}
              dangerouslySetInnerHTML={{
                __html: campaign.bodyHtml || "<em>No body content.</em>",
              }}
            />
          </Paper>
        )}
      </DialogContent>
    </Dialog>
    <Dialog
      open={Boolean(recipientToDelete)}
      onClose={() => (recipientDeleting ? undefined : setRecipientToDelete(null))}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        Remove recipient?
        <IconButton
          aria-label="Close"
          onClick={() => setRecipientToDelete(null)}
          disabled={recipientDeleting}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <LucideIcon name="X" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Remove <strong>{recipientToDelete?.email || "this recipient"}</strong>{" "}
          from this campaign? They will be excluded from any remaining sends and
          hidden from the recipients list and aggregate metrics.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setRecipientToDelete(null)}
          disabled={recipientDeleting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDeleteRecipient}
          color="error"
          variant="contained"
          disabled={recipientDeleting}
          startIcon={
            recipientDeleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LucideIcon name="Trash2" size={18} />
            )
          }
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CampaignDetailDialog;
